import { useCallback, useEffect, useRef, useState } from "react";
import * as defaultApi from "../api/coachApi";

/*
 * `GET /api/coach/home` (contract §5.1) and the small local patches the screen
 * applies between two reads: limits after a message, a memory count after a
 * save, consent after the consent screen, a check-in once submitted.
 * `status`: loading | ready | error. A failed read keeps the last good one.
 */
export default function useCoachHome({ api = defaultApi } = {}) {
  const [home, setHome] = useState(null);
  const [status, setStatus] = useState("loading");
  const requestRef = useRef(0);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const request = ++requestRef.current;
    if (!quiet) setStatus("loading");
    try {
      const next = await api.getCoachHome();
      if (request !== requestRef.current) return null;
      setHome(next);
      setStatus("ready");
      return next;
    } catch (error) {
      if (request !== requestRef.current) return null;
      setStatus((current) => (quiet && current === "ready" ? current : "error"));
      return null;
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = useCallback((updater) => {
    setHome((current) => (current?.availability === "AVAILABLE" ? updater(current) : current));
  }, []);

  const setLimits = useCallback((limits) => {
    if (!limits || (typeof limits.dailyMessages !== "number" && limits.unlimited !== true)) return;
    patch((current) => ({ ...current, limits: { ...current.limits, ...limits } }));
  }, [patch]);

  const setBudgetExhausted = useCallback(() => {
    patch((current) => ({ ...current, budget: { ...current.budget, state: "EXHAUSTED" } }));
  }, [patch]);

  const adjustMemoryCount = useCallback((delta) => {
    patch((current) => ({
      ...current,
      memories: { ...current.memories, activeCount: Math.max(0, (current.memories?.activeCount || 0) + delta) },
    }));
  }, [patch]);

  const setMemoryCount = useCallback((activeCount) => {
    patch((current) => ({ ...current, memories: { ...current.memories, activeCount } }));
  }, [patch]);

  const applySettings = useCallback((response) => {
    patch((current) => ({
      ...current,
      settings: response?.settings || current.settings,
      consent: response?.consent || current.consent,
    }));
  }, [patch]);

  const markCheckinSubmitted = useCallback((eventId) => {
    patch((current) => ({
      ...current,
      checkin: { ...current.checkin, due: false, submittedEventId: eventId || current.checkin?.submittedEventId || null },
    }));
  }, [patch]);

  return {
    home,
    status,
    reload: load,
    setLimits,
    setBudgetExhausted,
    adjustMemoryCount,
    setMemoryCount,
    applySettings,
    markCheckinSubmitted,
  };
}
